/* eslint-disable */
import React, {useEffect, useState} from "react";
import ReactMarkdown from "react-markdown";
import {useQuery} from "@tanstack/react-query";
import {LoadingOutlined, MenuOutlined} from '@ant-design/icons';
import {PiPaperPlaneTiltBold} from "react-icons/pi";
import {Button, Spin} from "antd";
import {useMediaQuery} from "@uidotdev/usehooks";

import {getCandidateAgentDetails} from "../../../services/api";
import {useAiJobs} from "../../../hooks/useAiJobs";
import {
    ChatContainer,
    ChatLayoutContainer,
    ErrorMessage,
    InputArea,
    MessageBubble,
    MessageInput,
    MessagesContainer,
    MessageTextArea,
    MessageWrapper,
    SendButton,
    TypingIndicator
} from "./StyledComponents";

import {
    ChatSession,
    useAutoFocus,
    useAutoScroll,
    useChatAuth,
    useChatSessions,
    useChatState,
    useErrorHandling,
    useKeyboardHandling,
    useLocalStorage,
    useMessageHandling,
    useMessageUtils,
    useSessionJobDetails,
    useSessionMessages,
    useSessionTitleUpdater
} from "./hooks";

import {ChatAdapter} from "./adapters";
import {BaseChatSession} from "./types";
import {ChatSessionsSidebar} from "./ChatSessionsSidebar";
import {JobContextCard} from "./JobContextCard";
import styled from "styled-components";
import {colors} from "../../../util/colors.ts";

export interface GenericChatProps {
    adapter: ChatAdapter;
    customComponents?: React.ComponentType<any>[];
    useTextArea?: boolean;
    placeholder?: string;
    loadingText?: string;
    loginPrompt?: string;
    onStateChange?: (state: any) => void;
    initialContext?: any;
    className?: string;
    showSidebar?: boolean;
    sidebarAlwaysVisible?: boolean; // New prop to control sidebar behavior
    onSessionChange?: (sessionId: string) => void;
    initialSessionId?: string | null;
    onNewSession?: () => void;
    onShowSuggestedJobs?: () => void;
    conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax';
    hasActiveSubscription: boolean;
}

const DisclaimerText = styled.p`
    font-size: 12px;
    font-style: italic;
    color: ${colors.darkGreen};
    margin: 0 0 8px 16px;
`;

const MobileSidebarButton = styled.div<{ alwaysVisible?: boolean }>`
    display: ${({alwaysVisible}) => (alwaysVisible ? 'none' : 'block')};

    @media (max-width: 991px) {
        display: block;
    }
`;

export const GenericChat: React.FC<GenericChatProps> = ({
                                                            adapter,
                                                            customComponents = [],
                                                            useTextArea = false,
                                                            placeholder,
                                                            loginPrompt = "Please log in to start chatting with SilverAgent.",
                                                            onStateChange,
                                                            initialContext,
                                                            className,
                                                            showSidebar = false,
                                                            sidebarAlwaysVisible = false,
                                                            onSessionChange,
                                                            initialSessionId,
                                                            onNewSession,
                                                            onShowSuggestedJobs,
                                                            conversationType,
                                                            hasActiveSubscription
                                                        }) => {
    // Use shared hooks
    const {
        message,
        setMessage,
        chatSession,
        setChatSession,
        isLoading,
        setIsLoading,
        error,
        setError,
        setShowNewConversationButton
    } = useChatState<BaseChatSession>();

    const {inputRef, focusInput} = useMessageHandling();
    const {isLoggedIn, candidateId} = useChatAuth();
    const {handleError, createErrorMessage} = useErrorHandling();
    const {createUserMessage, createAgentMessage} = useMessageUtils();

    // Mobile detection and autoscroll control
    const isMobile = useMediaQuery("only screen and (max-width : 991px)");
    const enableAutoScroll = !(isMobile && !hasActiveSubscription);
    const {messagesEndRef} = useAutoScroll(chatSession?.messages || [], enableAutoScroll);

    // React Query for AI jobs
    const {data: aiJobs, refetch: refetchAiJobs, error: jobsError} = useAiJobs(candidateId);

    // Feature-specific state
    const [featureState, setFeatureState] = useState(() => adapter.getInitialState());

    // Session management state
    const [activeSessionId, setActiveSessionId] = useState<string | null>(initialSessionId || null);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
        `chat-sidebar-collapsed-${conversationType || 'default'}`,
        false
    );

    // Track if we've finished checking for existing sessions
    const [hasCheckedForExistingSessions, setHasCheckedForExistingSessions] = useState(false);

    // Load session messages when active session changes
    const {messages: sessionMessages} = useSessionMessages(activeSessionId, conversationType);

    // Get all sessions to find current session context
    const {sessions} = useChatSessions(conversationType);

    // Find current session to check completion status
    const currentSession = sessions?.find((session: any) => session.id === activeSessionId);
    const isSessionComplete = currentSession?.context_data?.stage === 'complete';

    // Update session title when reaching 5 messages (once per session)
    useSessionTitleUpdater(activeSessionId, chatSession?.messages || [], conversationType);

    // Fetch job details for existing cover letter sessions
    const {jobDetails: sessionJobDetails} = useSessionJobDetails(activeSessionId, conversationType);

    // Update active session when initialSessionId changes
    useEffect(() => {
        if (initialSessionId !== activeSessionId) {
            setActiveSessionId(initialSessionId || null);
        }
        // Mark as checked once initialSessionId has been resolved (even if null)
        if (initialSessionId !== undefined) {
            setHasCheckedForExistingSessions(true);
        }
    }, [initialSessionId, activeSessionId]);

    // Get agent details (preserve existing pattern)
    const {data: agentDetails, isLoading: isAgentDetailsLoading} = useQuery({
        queryKey: ['candidateAgentDetails', candidateId],
        queryFn: () => getCandidateAgentDetails(String(candidateId!)),
        enabled: !!candidateId
    });

    // Auto-focus (preserve existing behavior)
    useAutoFocus(isLoading, inputRef);

    // Load session messages into chat when session changes
    useEffect(() => {
        if (activeSessionId && Array.isArray(sessionMessages) && sessionMessages.length > 0) {
            const formattedMessages = sessionMessages.map((msg: any) => ({
                id: msg.id?.toString() || Date.now().toString(),
                text: msg.message || msg.text || '',
                sender: (msg.role === 'user' ? 'user' : 'agent') as 'user' | 'agent',
                timestamp: new Date(msg.created_at || Date.now()),
                stage: msg?.metadata
            }));

            setChatSession({
                sessionId: activeSessionId,
                messages: formattedMessages
            });
        }
    }, [activeSessionId, sessionMessages]);

    // Start session when ready - ONLY after confirming no existing sessions
    useEffect(() => {
        if (isLoggedIn && agentDetails && !chatSession && !activeSessionId &&
            hasCheckedForExistingSessions && initialSessionId === null) {
            startNewSession();
        }
    }, [isLoggedIn, agentDetails, chatSession, activeSessionId, hasCheckedForExistingSessions, initialSessionId]);

    // Notify parent of state changes
    useEffect(() => {
        onStateChange?.(featureState);
    }, [featureState, onStateChange]);

    const startSession = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Prepare context based on adapter type and initial context
            const context = {
                userProfile: agentDetails,
                ...initialContext
            };

            const response = await adapter.startSession(context);

            const newSessionId = response.sessionId;
            setActiveSessionId(newSessionId);
            onSessionChange?.(newSessionId);

            setChatSession({
                sessionId: newSessionId,
                messages: [createAgentMessage(response.initialMessage)]
            });

            if (response.initialState) {
                setFeatureState((prev: any) => ({...prev, ...response.initialState}));
            }
        } catch (error) {
            const errorMessage = handleError(error);
            setError(errorMessage);
            console.error("Failed to start chat session:", error);
        } finally {
            setIsLoading(false);
            setTimeout(focusInput, 100);
        }
    };

    const startNewSession = async () => {
        setShowNewConversationButton(false);
        setChatSession(null);
        setActiveSessionId(null);
        setFeatureState(adapter.getInitialState());
        onNewSession?.();
        await startSession();
    };

    const handleSessionSelect = (session: ChatSession) => {
        setActiveSessionId(session.id.toString());
        onSessionChange?.(session.id.toString());
    };

    const handleNewChat = (sessionId: string) => {
        setActiveSessionId(sessionId);
        onSessionChange?.(sessionId);

        setChatSession({
            sessionId,
            messages: []
        });
    };

    const sendMessage = async () => {
        if (!message.trim() || !chatSession) return;

        const messageText = message.trim();
        setMessage("");
        setError(null);

        const userMsg = createUserMessage(messageText);
        const updatedMessages = [...(chatSession.messages || []), userMsg];

        setChatSession({
            ...chatSession,
            messages: updatedMessages
        });

        setIsLoading(true);

        try {
            // Prepare context for adapters that need it (like Job Coach)
            const context = {
                userProfile: agentDetails,
                ...featureState
            };

            const response = await adapter.sendMessage(
                chatSession.sessionId,
                messageText,
                context
            );

            // Check if response indicates a timeout error
            const isTimeoutError = response?.data?.isTimeout === true;
            const isJobSearchConversation = conversationType === 'job_search';

            if (isTimeoutError && isJobSearchConversation) {
                // Graceful recovery: Jobs are likely ready, just the response timed out
                const recoveryMsg = createAgentMessage(
                    "Die Jobsuche dauert etwas länger als erwartet, aber Ihre Ergebnisse sind bereit! Ich lade jetzt Ihre passenden Jobs...",
                    1
                );

                setChatSession({
                    ...chatSession,
                    messages: [...updatedMessages, recoveryMsg]
                });

                // Mark as ready for job search
                setFeatureState((prev: any) => ({
                    ...prev,
                    readyForJobSearch: true
                }));

                // Auto-switch to jobs tab after short delay
                setTimeout(() => {
                    if (onShowSuggestedJobs) {
                        onShowSuggestedJobs();
                    }
                }, 1500);

                setIsLoading(false);
                setTimeout(focusInput, 100);
                return; // Exit early, don't process as normal response
            }

            const processedResponse = adapter.processResponse(response);
            const agentMsg = createAgentMessage(processedResponse.message, 1);

            setChatSession({
                ...chatSession,
                messages: [...updatedMessages, agentMsg]
            });

            if (processedResponse.action && processedResponse.data) {
                handleFeatureAction(processedResponse.action, processedResponse.data);
            }

            if (processedResponse.data?.suggestedActions) {
                setFeatureState((prev: any) => ({
                    ...prev,
                    suggestedActions: processedResponse.data.suggestedActions
                }));
            }

            if (processedResponse.data?.readyForJobSearch) {
                setFeatureState((prev: any) => ({
                    ...prev,
                    readyForJobSearch: processedResponse.data.readyForJobSearch
                }));

                // Auto-switch to jobs tab for job_search conversations
                if (conversationType === 'job_search' && onShowSuggestedJobs) {
                    onShowSuggestedJobs();
                }
            }

        } catch (error: any) {
            console.error('GenericChat sendMessage error:', error);

            // Normal error handling
            const errorMessage = handleError(error);
            setError(errorMessage);

            const errorMsg = createErrorMessage("Sorry, there was an error processing your message. Please try again.");
            setChatSession({
                ...chatSession,
                messages: [...updatedMessages, errorMsg]
            });
        } finally {
            setIsLoading(false);
            setTimeout(focusInput, 100);
        }
    };

    const handleFeatureAction = async (action: string, data: any) => {
        switch (action) {
            case 'FETCH_PINECONE_JOBS':
                if (candidateId) {
                    try {
                        await refetchAiJobs();

                        if (aiJobs) {
                            setFeatureState((prev: any) => ({
                                ...prev,
                                showJobsList: true,
                                matchedJobs: aiJobs,
                                jobs: aiJobs,
                                readyForJobSearch: true
                            }));
                        }
                    } catch (error) {
                        console.error('Failed to fetch pinecone jobs:', error);
                        setError('Failed to load jobs. Please try again.');
                    }
                }
                break;
            case 'SHOW_JOBS_LIST':
                setFeatureState((prev: any) => {
                    return {
                        ...prev,
                        showJobsList: true,
                        jobs: data.jobs,
                        matchedJobs: data.jobs
                    };
                });
                break;
            case 'SHOW_RECOMMENDATIONS':
                setFeatureState((prev: any) => ({
                    ...prev,
                    recommendations: data.recommendations
                }));
                break;
            case 'SESSION_COMPLETE':
                setShowNewConversationButton(true);
                setFeatureState((prev: any) => ({...prev, isComplete: true}));
                break;
            default:
                break;
        }
    };

    const {handleKeyPress} = useKeyboardHandling(sendMessage);

    const refreshJobs = async () => {
        if (candidateId) {
            try {
                const result = await refetchAiJobs();

                if (result.data) {
                    setFeatureState((prev: any) => ({
                        ...prev,
                        matchedJobs: result.data,
                        jobs: result.data,
                        jobsError: undefined
                    }));
                }
            } catch (error) {
                console.error('Failed to refresh jobs:', error);
                setFeatureState((prev: any) => ({
                    ...prev,
                    jobsError: 'Failed to refresh jobs. Please try again.'
                }));
            }
        }
    };

    // Update feature state when aiJobs becomes available
    useEffect(() => {
        if (aiJobs && aiJobs.length > 0) {
            setFeatureState((prev: any) => ({
                ...prev,
                matchedJobs: aiJobs,
                jobs: aiJobs,
                hasStoredJobs: true,
                jobsError: undefined
            }));
        }
    }, [aiJobs]);

    // Handle jobs error
    useEffect(() => {
        if (jobsError) {
            setFeatureState((prev: any) => ({
                ...prev,
                jobsError: 'Failed to load jobs. Please try again.'
            }));
        }
    }, [jobsError]);


    const renderMessageContent = (text: string) => {
        return <ReactMarkdown>{text}</ReactMarkdown>;
    };

    const renderCustomComponents = () => {
        return customComponents.map((Component, index) => (
            <Component
                key={index}
                state={featureState}
                onStateChange={setFeatureState}
                messages={chatSession?.messages || []}
                onSendMessage={sendMessage}
                onRefreshJobs={refreshJobs}
            />
        ));
    };

    const getDefaultPlaceholder = () => {
        if (!isLoggedIn) return "Please log in to chat...";
        return placeholder || "Type a message...";
    };

    const InputComponent = useTextArea ? MessageTextArea as any : MessageInput as any;

    // Render chat content (reusable)
    const chatContent = (
        <>
            {showSidebar && (
                <MobileSidebarButton alwaysVisible={sidebarAlwaysVisible}
                                     style={{position: 'absolute', top: '16px', right: '16px', zIndex: 1000}}>
                    <Button
                        icon={<MenuOutlined/>}
                        onClick={() => setSidebarVisible(true)}
                        type="text"
                    />
                </MobileSidebarButton>
            )}

            <MessagesContainer>
                {conversationType === 'job_letter' && (() => {
                    // Priority: 1) Existing session job details, 2) Initial context (new sessions), 3) localStorage (backward compatibility)
                    let jobId: string | null = null;
                    let jobTitle: string | null = null;

                    if (sessionJobDetails) {
                        // Use job details fetched for existing sessions
                        jobId = sessionJobDetails.jobId;
                        jobTitle = sessionJobDetails.jobTitle;
                    } else {
                        // For new sessions without existing job context, no job info available
                        jobId = initialContext?.job_demand_id?.toString();
                        jobTitle = null;

                        // Extract title from jobRole if available from initial context
                        if (initialContext?.jobRole) {
                            const jobRole = initialContext.jobRole;
                            // Extract the first line as title, or first part before newlines/description
                            const lines = jobRole.split('\n').filter((line: string) => line.trim());
                            jobTitle = lines[0]?.trim() || 'Job Position';
                        }
                    }

                    return jobId && jobTitle ? (
                        <JobContextCard jobId={jobId} jobTitle={jobTitle}/>
                    ) : null;
                })()}
                {isAgentDetailsLoading ? (
                    <MessageWrapper sender="agent">
                        <MessageBubble sender="agent">
                            Loading your profile...
                        </MessageBubble>
                    </MessageWrapper>
                ) : !isLoggedIn ? (
                    <MessageWrapper sender="agent">
                        <MessageBubble sender="agent">
                            {loginPrompt}
                        </MessageBubble>
                    </MessageWrapper>
                ) : (
                    <>
                        {chatSession?.messages.map((msg) => (
                            <MessageWrapper key={msg.id} sender={msg.sender}>
                                <MessageBubble sender={msg.sender}>
                                    {renderMessageContent(msg.text)}
                                    {/* Show suggested jobs button after last message if readyForJobSearch */}
                                    {/* For job_search, auto-redirect is enabled, so button is not needed */}
                                    {msg.sender === 'agent' && conversationType == 'job_search' && msg.stage === "ready" && (
                                        <div style={{marginTop: '12px', display: 'flex', justifyContent: 'center'}}>
                                            <Button
                                                type="default"
                                                onClick={onShowSuggestedJobs}
                                            >
                                                Jobs anzeigen
                                            </Button>
                                        </div>
                                    )}
                                </MessageBubble>

                            </MessageWrapper>
                        ))}

                        {/* Render custom components for feature-specific UI */}
                        {renderCustomComponents()}

                        {error && (
                            <MessageWrapper sender="agent">
                                <ErrorMessage sender="agent">
                                    {error}
                                </ErrorMessage>
                            </MessageWrapper>
                        )}

                        {isLoading && (
                            <TypingIndicator>
                                <Spin indicator={<LoadingOutlined spin/>} size="small"/>
                            </TypingIndicator>
                        )}
                    </>
                )}

                <div ref={messagesEndRef}/>
            </MessagesContainer>
            <InputArea>

                <>
                    <InputComponent
                        type={useTextArea ? undefined : "text"}
                        autoSize={useTextArea ? {minRows: 1, maxRows: 8} : undefined}
                        placeholder={getDefaultPlaceholder()}
                        value={message}
                        autoFocus={hasActiveSubscription}
                        onChange={(e: any) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading || !isLoggedIn || isAgentDetailsLoading || isSessionComplete || !hasActiveSubscription}
                        ref={inputRef}
                    />
                    <SendButton
                        onClick={sendMessage}
                        disabled={!message.trim() || isLoading || !isLoggedIn || isAgentDetailsLoading || isSessionComplete || !hasActiveSubscription}
                    >
                        <PiPaperPlaneTiltBold size={22}/>
                    </SendButton>
                </>
            </InputArea>
            {conversationType === 'job_tax' && (
                <DisclaimerText>Der Steuer & Rente Experte kann Fehler machen. Überprüfen Sie wichtige
                    Informationen.</DisclaimerText>
            )}
        </>
    );

    // Render with always-visible sidebar layout
    if (showSidebar && sidebarAlwaysVisible) {
        return (
            <>
                <ChatLayoutContainer className={className}>
                    <ChatContainer withSidebar sidebarCollapsed={sidebarCollapsed}>
                        {chatContent}
                    </ChatContainer>
                    <ChatSessionsSidebar
                        visible={true}
                        alwaysVisible={true}
                        collapsed={sidebarCollapsed}
                        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                        onClose={() => {
                        }}
                        onSessionSelect={handleSessionSelect}
                        onNewChat={handleNewChat}
                        activeSessionId={activeSessionId || undefined}
                        conversationType={conversationType}
                    />
                </ChatLayoutContainer>
                {/* Mobile drawer sidebar */}
                <ChatSessionsSidebar
                    visible={sidebarVisible}
                    onClose={() => setSidebarVisible(false)}
                    onSessionSelect={handleSessionSelect}
                    onNewChat={handleNewChat}
                    activeSessionId={activeSessionId || undefined}
                    conversationType={conversationType}
                />
            </>
        );
    }

    // Render with drawer sidebar (default)
    return (
        <>
            <ChatContainer className={className}>
                {chatContent}
            </ChatContainer>

            {showSidebar && !sidebarAlwaysVisible && (
                <ChatSessionsSidebar
                    visible={sidebarVisible}
                    onClose={() => setSidebarVisible(false)}
                    onSessionSelect={handleSessionSelect}
                    onNewChat={handleNewChat}
                    activeSessionId={activeSessionId || undefined}
                    conversationType={conversationType}
                />
            )}
        </>
    );
};
